import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { Empty, Form, Input, Select, Tooltip, Typography } from 'antd'
import useDirectory from 'hooks/useDirectory'
import ViewerFilePreview from 'pages/building/buildingDetailViewMode/components/ViewerFilePreview'
import PreviewFile from 'pages/building/panoramas/components/PreviewFile'
import useSWR from 'swr'
import {
  Folders,
  SearchCategoryChild,
  SearchFile,
  SearchFolder,
  SearchResponse
} from 'types/building'
import { Memo } from 'types/memo'

import Button from 'components/atoms/Button'
import Spinner from 'components/atoms/Spinner'
import ViewFile360Icon from 'components/icons/360ViewFileIcon'
import MemoFileIcon from 'components/icons/MemoFIleIcon'
import RightArrowIcon from 'components/icons/RightArrowIcon'

import FolderIcon from './FolderIcon'
import MemoViewModal from './MemoViewModal'
import ModalListRelatedPoints from './ModalListRelatedPoints'

function ParentGroupHeader({
  label,
  id = undefined
}: {
  label: string
  id?: number
}) {
  return (
    <div className="theme-bg-primary h-[40px] px-5 flex items-center gap-4">
      {id === 40 ? (
        <MemoFileIcon />
      ) : (
        id !== undefined && <FolderIcon type={id} size="small" />
      )}
      <Typography className="text-white font-bold text-[14px]">
        {label}
      </Typography>
    </div>
  )
}

ParentGroupHeader.defaultProps = {
  id: undefined
}

function SubFolderHeader({ label }: { label: string }) {
  return (
    <div className="bg-[#EEEFF2] h-[32px] px-5 flex items-center">
      <Typography className="text-[#021120] font-bold text-[13px]">
        {label}
      </Typography>
    </div>
  )
}

interface SearchFileItemProps {
  file: SearchFile
  onPreview: (file: SearchFile) => void
  onShow360: (file: SearchFile) => void
  primaryColor: string
  t: (key: string) => string
}

function SearchFileItem({
  file,
  onPreview,
  onShow360,
  primaryColor,
  t
}: SearchFileItemProps) {
  return (
    <div className="flex items-center gap-5 justify-between min-h-[80px] p-[10px] border-0 border-b border-solid border-[#EEEFF2]">
      <div
        className="flex items-center gap-5 cursor-pointer"
        onClick={() => onPreview(file)}
      >
        <div className="flex-shrink-0">
          <PreviewFile
            file={file}
            size={file.extension.toLowerCase() === 'pdf' ? 'default' : 'large'}
          />
        </div>
        <Typography className="font-medium text-[#021120]">
          {file.name}
        </Typography>
      </div>
      {file.isRelated && (
        <Button
          type="primary"
          color="primary"
          className="related-point-button gap-[3px] py-[7px] px-[3px] mr-[28px]"
          onClick={() => onShow360(file)}
          sizing="w-[160px] h-[40px]"
        >
          <ViewFile360Icon fill={primaryColor} />
          {t('building.360ViewFile')}
        </Button>
      )}
    </div>
  )
}

interface SearchMemoItemProps {
  memo: Memo
  onViewDetail: (memo: Memo) => void
}

function SearchMemoItem({ memo, onViewDetail }: SearchMemoItemProps) {
  const getMemoDisplayName = (memo: Memo) => {
    const floorName = `${memo.floorName}`
    const pointOrder = `${memo.pointOrder}`
    return `${floorName}_ポイント ${pointOrder}_${
      memo.memoName || memo.annotationId
    }`
  }

  const displayName = getMemoDisplayName(memo)

  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors mb-3 bg-white mx-5 mt-2">
      <div
        className="flex-1 min-w-0 cursor-pointer"
        onClick={() => onViewDetail(memo)}
      >
        <Tooltip title={displayName}>
          <div className="text-sm font-bold text-gray-900 truncate max-w-[600px]">
            {displayName}
          </div>
        </Tooltip>
        <div className="text-xs text-gray-500 mt-1">
          {new Date(memo.updatedAt).toLocaleDateString('ja-JP')}
        </div>
      </div>
      <Button
        type="primary"
        onClick={() => onViewDetail(memo)}
        className="ml-4 h-[38px] px-6 rounded-[8px]"
      >
        詳細を表示
      </Button>
    </div>
  )
}

type Props = {
  listFolder: Folders[]
}

export default function ListFolder({ listFolder }: Props) {
  const { id } = useParams()
  const { t } = useTranslation()
  const { getDirectory, getDirectoryPrimaryColor } = useDirectory()
  const [form] = Form.useForm()

  const domain = useMemo(() => {
    const dir = getDirectory()
    return dir === 'shimizu' ? '360' : dir
  }, [getDirectory])
  const [searchParams, setSearchParams] = useSearchParams()
  const folder = searchParams.get('folder')
  const keyword = searchParams.get('keyword')
  const categoryParam = searchParams.get('category')
  const childrenParam = searchParams.get('children')
  const isSearchParam = searchParams.get('is_search')
  const previewIndex = searchParams.get('preview_idx')
  const ver = searchParams.get('ver')

  const [selectedFile, setSelectedFile] = useState<
    string | number | undefined
  >()
  const [selectedMemo, setSelectedMemo] = useState<Memo | null>(null)
  const [memoViewVisible, setMemoViewVisible] = useState(false)

  const isSearching = Boolean(
    keyword || categoryParam || childrenParam || isSearchParam
  )

  // Determine the actual children ID for the API call based on URL params (not form state)
  const actualSearchChildrenId = useMemo(() => {
    if (!childrenParam) return undefined

    // If a category is selected in URL, find within that category
    if (categoryParam) {
      const parent = listFolder.find((f) => f.id === Number(categoryParam))
      const child = parent?.children?.find((c) => c.label === childrenParam)
      return child?.categoryId ?? child?.id
    }

    // If no category selected in URL, find the first occurrence of this child label
    let childId: number | undefined
    listFolder.forEach((parent) => {
      if (childId) return
      const child = parent.children?.find((c) => c.label === childrenParam)
      if (child) {
        childId = child.categoryId ?? child.id
      }
    })
    return childId
  }, [categoryParam, childrenParam, listFolder])

  const { data: searchResults, isValidating } = useSWR<SearchResponse>(
    isSearching && !folder && id
      ? {
          url: 'v1/buildings/files/search',
          params: {
            building_id: id,
            keyword: keyword || undefined,
            category: categoryParam || undefined,
            children: actualSearchChildrenId,
            domain,
            ver: ver || undefined,
            page: 1,
            page_size: 1000
          }
        }
      : null
  )

  const groupedSearchData = useMemo(() => {
    // Handle case where fetcher might return the data array directly
    const rawData = Array.isArray(searchResults)
      ? searchResults
      : (searchResults as any)?.data || []

    return rawData
  }, [searchResults])

  const allFiles = useMemo(() => {
    if (!groupedSearchData || !Array.isArray(groupedSearchData)) return []
    const files: SearchFile[] = []
    const extractFiles = (item: any) => {
      const category = item.category || item
      if (category.files) files.push(...category.files)
      if (category.folder) {
        const folderArray = Array.isArray(category.folder)
          ? category.folder
          : [category.folder]
        folderArray.forEach((f: any) => {
          if (f.files) files.push(...f.files)
        })
      }
      if (Array.isArray(category.children)) {
        category.children.forEach((child: any) => extractFiles(child))
      }
    }
    groupedSearchData.forEach((item: any) => extractFiles(item))
    return files
  }, [groupedSearchData])

  useEffect(() => {
    form.setFieldsValue({
      keyword: keyword || '',
      category: categoryParam ? Number(categoryParam) : undefined,
      children: childrenParam || undefined
    })
  }, [form, keyword, categoryParam, childrenParam])

  const previewFile = useMemo(() => {
    if (previewIndex && allFiles[+previewIndex]) {
      return allFiles[+previewIndex]
    }
    return undefined
  }, [previewIndex, allFiles])

  const handleSelectPreview = (index: number) => {
    setSearchParams((prev) => {
      prev.set('preview_idx', `${index}`)
      return prev
    })
  }

  const handlePreview = (file: SearchFile) => {
    const index = allFiles.findIndex((f) => f.id === file.id)
    if (index !== -1) {
      handleSelectPreview(index)
    }
  }

  // 注釈タイプ options = top-level folder categories
  const parentFolderOptions = useMemo(() => {
    return listFolder.map((f) => ({
      label: f.label,
      value: f.id
    }))
  }, [listFolder])

  const childFolderOptions = useMemo(() => {
    const allChildrenLabels = new Set<string>()
    listFolder.forEach((parent) => {
      parent.children?.forEach((child) => {
        if (child.label) {
          allChildrenLabels.add(child.label)
        }
      })
    })

    return Array.from(allChildrenLabels).map((label) => ({
      label,
      value: label
    }))
  }, [listFolder])

  const handleSearch = (
    values: Record<string, string | number | undefined>
  ) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete('preview_idx') // Reset preview on new search
        if (values.keyword) {
          newParams.set('keyword', `${values.keyword}`.trim())
        } else {
          newParams.delete('keyword')
        }
        if (values.category !== undefined && values.category !== null) {
          newParams.set('category', `${values.category}`)
        } else {
          newParams.delete('category')
        }
        if (values.children !== undefined && values.children !== null) {
          newParams.set('children', `${values.children}`)
        } else {
          newParams.delete('children')
        }
        newParams.set('ver', Date.now().toString())
        newParams.set('is_search', '1')
        return newParams
      },
      { replace: true }
    )
  }

  const handleSelectCategory = (value: number | undefined) => {
    form.setFieldValue('category', value)
    if (value === 40) {
      form.setFieldValue('children', undefined)
    }
    handleSearch(form.getFieldsValue())
  }

  const handleSelectChildren = (value: string | undefined) => {
    form.setFieldValue('children', value)
    if (!value) {
      handleSearch(form.getFieldsValue())
      return
    }
    // Find all parent categories that contain this specific child label
    const possibleParents = listFolder.filter(
      (f) => f.children?.some((child) => child.label === value)
    )

    if (possibleParents.length === 1) {
      form.setFieldValue('category', possibleParents[0].id)
    } else {
      // If multiple parents (like '取扱説明書'), don't auto-set, let user pick from filtered parentFolderOptions
      // but only if the current category is NOT one of the possible parents
      const currentCategory = form.getFieldValue('category')
      if (!possibleParents.some((p) => p.id === currentCategory)) {
        form.setFieldValue('category', undefined)
      }
    }
    handleSearch(form.getFieldsValue())
  }

  const handleReset = () => {
    form.resetFields()
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete('keyword')
        newParams.delete('category')
        newParams.delete('children')
        newParams.delete('is_search')
        newParams.delete('preview_idx')
        newParams.delete('ver')
        return newParams
      },
      { replace: true }
    )
  }

  const totalNumberOfFile = useMemo(
    () =>
      listFolder.reduce((count, item) => {
        return count + (item.numberOfFile || 0)
      }, 0),
    [listFolder]
  )

  const hanldeSelectFolder = (folder: Folders) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      newParams.set('folder', `${folder.id}`)
      return newParams
    })
  }

  if (folder) return null

  return (
    <>
      <Form
        form={form}
        preserve={false}
        onFinish={handleSearch}
        className="search-form-premium"
      >
        <div className="building-file-search-bar flex items-center flex-wrap rounded-[4px] bg-white border border-solid border-[#D9D9D9] px-6 py-3 gap-8">
          <div className="flex items-center gap-[8px]">
            <Typography className="text-[13px] text-[#021120] font-bold whitespace-nowrap">
              キーワード
            </Typography>
            <Form.Item name="keyword" className="mb-0 thin-error-message">
              <Input className="w-[180px] h-[38px] rounded-[8px]" />
            </Form.Item>
          </div>
          <div className="flex items-center gap-[8px]">
            <Typography className="text-[13px] text-[#021120] font-bold whitespace-nowrap">
              注釈タイプ
            </Typography>
            <Form.Item name="category" className="mb-0">
              <Select
                allowClear
                options={parentFolderOptions}
                onChange={handleSelectCategory}
                style={{ width: 180, height: 38 }}
                className="rounded-[8px]"
              />
            </Form.Item>
          </div>
          <div className="flex items-center gap-[8px]">
            <Typography className="text-[13px] text-[#021120] font-bold whitespace-nowrap">
              分類
            </Typography>
            <Form.Item name="children" className="mb-0">
              <Select
                allowClear
                options={childFolderOptions}
                onChange={handleSelectChildren}
                disabled={
                  childFolderOptions.length === 0 ||
                  form.getFieldValue('category') === 40
                }
                style={{ width: 180, height: 38 }}
                className="rounded-[8px]"
              />
            </Form.Item>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            className="h-[38px] px-8 rounded-[8px] font-bold"
            loading={isValidating}
          >
            検索
          </Button>
          <Button
            type="primary"
            htmlType="button"
            onClick={handleReset}
            className="h-[38px] px-8 rounded-[8px] font-bold"
            loading={isValidating}
          >
            リセット
          </Button>
        </div>
      </Form>

      {isSearching ? (
        <div className="overflow-auto bg-white flex-1 min-h-0 relative">
          {isValidating && <Spinner spinning={isValidating} />}
          {!isValidating && isSearching && groupedSearchData.length === 0 && (
            <div className="py-10">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('building.fileNotFound')}
              />
            </div>
          )}

          {groupedSearchData.map((item: any, idx: number) => {
            const category = item.category || item
            if (!category || !category.label) return null
            return (
              <div key={category.id || idx} className="mb-5">
                <ParentGroupHeader label={category.label} id={category.id} />

                {/* Files directly under category */}
                {category.files?.map((file: SearchFile) => (
                  <SearchFileItem
                    key={file.id}
                    file={file}
                    onPreview={handlePreview}
                    onShow360={(f: SearchFile) => setSelectedFile(f.id)}
                    primaryColor={getDirectoryPrimaryColor()}
                    t={t}
                  />
                ))}

                {/* Memos under category */}
                {category.memos?.map((memo: Memo) => (
                  <SearchMemoItem
                    key={memo.id}
                    memo={memo}
                    onViewDetail={(m) => {
                      setSelectedMemo(m)
                      setMemoViewVisible(true)
                    }}
                  />
                ))}

                {/* Folders directly under category */}
                {category.folder?.map((folder: SearchFolder) => (
                  <div key={folder.id}>
                    <SubFolderHeader label={folder.name} />
                    {folder.files?.map((file: SearchFile) => (
                      <SearchFileItem
                        key={file.id}
                        file={file}
                        onPreview={handlePreview}
                        onShow360={(f: SearchFile) => setSelectedFile(f.id)}
                        primaryColor={getDirectoryPrimaryColor()}
                        t={t}
                      />
                    ))}
                    {folder.memos?.map((memo: Memo) => (
                      <SearchMemoItem
                        key={memo.id}
                        memo={memo}
                        onViewDetail={(m) => {
                          setSelectedMemo(m)
                          setMemoViewVisible(true)
                        }}
                      />
                    ))}
                  </div>
                ))}

                {/* Sub-categories (children) */}
                {category.children?.map((child: SearchCategoryChild) => (
                  <div key={child.id}>
                    <SubFolderHeader label={child.label} />
                    {child.files?.map((file: SearchFile) => (
                      <SearchFileItem
                        key={file.id}
                        file={file}
                        onPreview={handlePreview}
                        onShow360={(f: SearchFile) => setSelectedFile(f.id)}
                        primaryColor={getDirectoryPrimaryColor()}
                        t={t}
                      />
                    ))}
                    {child.memos?.map((memo: Memo) => (
                      <SearchMemoItem
                        key={memo.id}
                        memo={memo}
                        onViewDetail={(m) => {
                          setSelectedMemo(m)
                          setMemoViewVisible(true)
                        }}
                      />
                    ))}
                    {child.folder?.map((folder: SearchFolder) => (
                      <div key={folder.id}>
                        {folder.files?.map((file: SearchFile) => (
                          <SearchFileItem
                            key={file.id}
                            file={file}
                            onPreview={handlePreview}
                            onShow360={(f: SearchFile) => setSelectedFile(f.id)}
                            primaryColor={getDirectoryPrimaryColor()}
                            t={t}
                          />
                        ))}
                        {folder.memos?.map((memo: Memo) => (
                          <SearchMemoItem
                            key={memo.id}
                            memo={memo}
                            onViewDetail={(m) => {
                              setSelectedMemo(m)
                              setMemoViewVisible(true)
                            }}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      ) : (
        <>
          <div className="bg-white">
            {listFolder.map((item, index) => (
              <div
                key={item.id}
                className={`h-[60px] px-5 flex items-center gap-[16px] border-0 border-b ${
                  !index ? 'border-t' : ''
                } border-solid border-[#EEEFF2] cursor-pointer`}
                onClick={() => hanldeSelectFolder(item)}
              >
                <div className="h-[36px] w-[36px] flex justify-center">
                  <FolderIcon type={item.id} size="large" />
                </div>
                <Typography className="font-medium text-[#021120]">
                  {item.label}
                </Typography>
                <div className="ml-auto flex gap-3">
                  <div
                    className={`font-medium text-base ${
                      !item.numberOfFile ? 'text-[#A6AAAF]' : 'text-[#3B3C46]'
                    }`}
                  >
                    {item.numberOfFile}ファイル
                  </div>
                  <RightArrowIcon color="#3B3C46" size={24} />
                </div>
              </div>
            ))}
          </div>
          {Boolean(listFolder.length) && (
            <div className="flex mr-3 ml-auto">
              <div className="text-[#3B3C46] text-base font-medium">
                合計：{totalNumberOfFile}ファイル
              </div>
            </div>
          )}
        </>
      )}

      {previewFile && (
        <ViewerFilePreview
          fileIndex={undefined}
          setPreviewFileIndex={undefined}
          file={previewFile}
          maxLength={allFiles.length}
        />
      )}

      <ModalListRelatedPoints
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
      />

      <MemoViewModal
        visible={memoViewVisible}
        memo={selectedMemo}
        onClose={() => {
          setMemoViewVisible(false)
          setSelectedMemo(null)
        }}
      />
    </>
  )
}
